// AGC: Lightweight hook for lot card weather (temp + rain).
// AGC: Reuses weatherService cache (shared with useLotWeatherHealth in LotPage).

import { useState, useEffect } from 'react'
import { getWeatherData } from '../services/weatherService'
import type { Plot } from '../types'

export interface LotCardWeather {
  temperature: number
  rain24h: number
}

export const useLotCurrentWeather = (
  plot: Plot | null,
  farmCoords?: [number, number],
): LotCardWeather | null => {
  const [data, setData] = useState<LotCardWeather | null>(null)

  useEffect(() => {
    let cancelled = false

    // AGC: Resolve coordinates — polygon centroid → farm coords
    let lat: number | undefined
    let lon: number | undefined

    if (plot?.coordinates && plot.coordinates.length > 0) {
      let sumLat = 0
      let sumLng = 0
      plot.coordinates.forEach(([la, ln]) => {
        sumLat += la
        sumLng += ln
      })
      lat = sumLat / plot.coordinates.length
      lon = sumLng / plot.coordinates.length
    } else if (farmCoords) {
      lat = farmCoords[0]
      lon = farmCoords[1]
    }

    if (lat == null || lon == null) {
      setData(null)
      return
    }

    getWeatherData(lat, lon, 'free')
      .then(weather => {
        if (cancelled) return
        const temp = weather.current?.temperature
        const rain = weather.current?.precipitation
        if (typeof temp !== 'number' || typeof rain !== 'number') {
          setData(null)
          return
        }
        setData({
          temperature: Math.round(temp),
          rain24h: Math.round(rain * 10) / 10,
        })
      })
      .catch(() => {
        if (!cancelled) setData(null)
      })

    return () => {
      cancelled = true
    }
  }, [plot?.id, plot?.coordinates, farmCoords])

  return data
}

export default useLotCurrentWeather
